import React, { useRef, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { UI_TIMINGS } from '../../config/appConfig';
import { useCameraCaptures } from '../../contexts/cameraCapturesContext';
import './CameraModal.css';

export default function PreviewItem({ src, idx, selected, onSelect, onUpload, onDelete, onMatch, uploading, processing, boundingBox, boundingBoxes = [], clusterMetadata = null, showBoundingBox, immediateFaceMatch, matchFailed, captureId }) {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const hasTriggeredMatch = useRef(false);
  const clickTimerRef = useRef(null);
  const [clickPosition, setClickPosition] = React.useState(null);
  const [showClickIndicator, setShowClickIndicator] = React.useState(false);
  const { updateCapture } = useCameraCaptures();

  // Colors for multiple bounding boxes (one per cluster)
  // Expanded palette to support more clusters without reuse
  const boxColors = [
    '#66bb6a', // green
    '#42a5f5', // blue
    '#ffa726', // orange
    '#ab47bc', // purple
    '#ef5350', // red
    '#26c6da', // cyan
    '#ffee58', // yellow
    '#ec407a', // pink
    '#9ccc65', // light green
    '#7e57c2', // deep purple
    '#ff7043', // deep orange
    '#29b6f6'  // light blue
  ];

  const drawBoundingBoxes = () => {
    // Use boundingBoxes array if available, otherwise fall back to single boundingBox
    const boxes = boundingBoxes.length > 0 ? boundingBoxes : (boundingBox ? [boundingBox] : []);

    if (boxes.length > 0 && showBoundingBox && canvasRef.current && imageRef.current) {
      const canvas = canvasRef.current;
      const image = imageRef.current;

      // Wait for image to have dimensions
      if (image.width === 0 || image.height === 0) return;

      const ctx = canvas.getContext('2d');

      // Set canvas size to match image display size
      canvas.width = image.width;
      canvas.height = image.height;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw bounding boxes directly from cluster structure
      if (clusterMetadata?.clusters) {
        clusterMetadata.clusters.forEach(cluster => {
          const clusterIndex = cluster.cluster_index;
          const color = boxColors[clusterIndex % boxColors.length];

          cluster.faces.forEach((face, faceIdx) => {
            const box = face.boundingBox;

            // AWS Rekognition bounding box coordinates are ratios (0-1)
            const x = box.Left * canvas.width;
            const y = box.Top * canvas.height;
            const width = box.Width * canvas.width;
            const height = box.Height * canvas.height;

            ctx.strokeStyle = color;
            ctx.lineWidth = 3;
            ctx.strokeRect(x, y, width, height);
          });
        });
      } else if (boxes && boxes.length > 0) {
        // Fallback for backward compatibility (single box or legacy format)
        boxes.forEach((box, index) => {
          if (!box) return;

          const x = box.Left * canvas.width;
          const y = box.Top * canvas.height;
          const width = box.Width * canvas.width;
          const height = box.Height * canvas.height;

          ctx.strokeStyle = boxColors[0];
          ctx.lineWidth = 3;
          ctx.strokeRect(x, y, width, height);
        });
      }
    }
  };

  useEffect(() => {
    if (showBoundingBox) {
      drawBoundingBoxes();
    } else if (canvasRef.current) {
      // Clear canvas if bounding box is hidden
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [boundingBox, boundingBoxes, clusterMetadata, src, showBoundingBox]);

  useEffect(() => {
    // Redraw on window resize
    const handleResize = () => {
      drawBoundingBoxes();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [boundingBox, boundingBoxes, clusterMetadata, showBoundingBox]);

  useEffect(() => {
    // Trigger immediate face match when component first mounts if enabled
    // Only trigger once per preview item, and only if no bounding box exists yet
    if (immediateFaceMatch && !hasTriggeredMatch.current && !boundingBox && onMatch) {
      hasTriggeredMatch.current = true;
      // Small delay to ensure component is fully rendered
      setTimeout(() => onMatch(idx), UI_TIMINGS.MATCH_CALLBACK_DELAY);
    }
  }, [immediateFaceMatch, boundingBox, idx, onMatch]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="preview-item-root" onClick={() => onSelect(idx)}>
      <div className={`preview-image-frame ${selected ? 'selected' : ''}`}>
        <div className="preview-image-wrapper">
          <img
            ref={imageRef}
            src={src}
            alt={`capture-${idx}`}
            className="preview-image"
            onLoad={drawBoundingBoxes}
            onClick={(e) => {
              if (selected) {
                e.stopPropagation();

                // Calculate click position relative to the image
                const rect = e.currentTarget.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width) * 100;
                const y = ((e.clientY - rect.top) / rect.height) * 100;

                // Set click position and show indicator immediately
                setClickPosition({ x, y });
                setShowClickIndicator(true);

                // Store click position in capture object for face matching payload
                if (captureId && updateCapture) {
                  updateCapture(captureId, { clickPosition: { x, y } });
                }

                // Clear any existing timer
                if (clickTimerRef.current) {
                  clearTimeout(clickTimerRef.current);
                }

                // Hide indicator after animation completes (keep position for display)
                clickTimerRef.current = setTimeout(() => {
                  setShowClickIndicator(false);
                }, 1000);

                onMatch(idx);
              }
            }}
            style={{ cursor: selected ? 'pointer' : 'default' }}
          />
          {boundingBox && showBoundingBox && (
            <canvas
              ref={canvasRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none'
              }}
            />
          )}
          {boundingBox && (
            <AutoAwesomeIcon
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: '#ffa000',
                fontSize: 32,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                pointerEvents: 'none',
                zIndex: 10
              }}
            />
          )}

          {uploading && selected && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 3
              }}
            >
              <CircularProgress size={60} sx={{ color: 'white' }} />
            </Box>
          )}

          {processing && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                zIndex: 3
              }}
            >
              <CircularProgress size={40} sx={{ color: 'white' }} />
            </Box>
          )}

          {matchFailed && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 2,
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '141.42%',
                  height: '4px',
                  backgroundColor: 'red',
                  transform: 'translate(-50%, -50%) rotate(45deg)',
                  transformOrigin: 'center'
                }}
              />
            </Box>
          )}

          {clickPosition && showClickIndicator && (
            <Box
              sx={{
                position: 'absolute',
                top: `${clickPosition.y}%`,
                left: `${clickPosition.x}%`,
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: '3px solid #42a5f5',
                backgroundColor: 'rgba(66, 165, 245, 0.2)',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                zIndex: 5,
                animation: 'clickPulse 1s ease-out',
                '@keyframes clickPulse': {
                  '0%': {
                    transform: 'translate(-50%, -50%) scale(0.5)',
                    opacity: 1
                  },
                  '50%': {
                    transform: 'translate(-50%, -50%) scale(1.2)',
                    opacity: 0.8
                  },
                  '100%': {
                    transform: 'translate(-50%, -50%) scale(1)',
                    opacity: 0
                  }
                }
              }}
            />
          )}

          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onDelete(idx); }}
            sx={{
              position: 'absolute',
              bottom: '4px',
              right: '4px',
              zIndex: 2,
              backgroundColor: 'rgba(255,255,255,0.85)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,1)',
              }
            }}
            aria-label="delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
